import { createEffect, createMemo, createSignal, getOwner, runWithOwner, Show } from "solid-js";
import { useTranslation } from "../state/i18n";
import { DraggableFavourite, DraggableField } from "../components/field/draggable/Draggable";
import { isEmpty, isNil } from "lodash-es";
import FavouriteService from "../services/FavouriteService";
import InputModal from "../components/modal/InputModal";
import { InformativeModal } from "../components/modal/Modal";
import { useSearchParams } from "@solidjs/router";
import type { EditorSearchParams } from "../pages/formEditor/editor/EditorMenu";
import FieldId from "./FieldId";
import { useFormServices } from "../services/formServices";
import type { FormScope } from "../state/formVersion";
import type {
  BaseField,
  FavouriteMenuItemProps,
  FieldDataNameProperties,
  FieldLabelTextProperties,
  FieldState,
  Label,
  MenuItemProps,
  PreviewItemProps,
} from "./types";
import type { GenericSchema, InferOutput } from "valibot";
import { object, safeParse, unknown } from "valibot";
import { TextInput } from "../components/inputs/TextInput";
import { MAX_CHARACTERS_SM } from "../constants";
import { requireText } from "../utils/validation/base";
import FieldValueReference from "./FieldValueReference";
import { type PartialBy } from "../types";
import { getDynamicValueIcon } from "../utils/menu/optionIconUtil";
import { useModalManager } from "../components/modal/ModalManager";
import { useFormEditorSidePanel } from "../pages/formEditor/FormEditorContent";
import Handlebar from "../components/field/Handlebar";
import { useDragDropContext } from "../utils/dragDropUtil";
import FloatingToolbar from "../components/toolbar/FloatingToolbar";
import FieldDragOverlay from "../components/field/propertyContainer/PropertyContainerOverlay";
import { getPanelCloseButtonId } from "../components/panels/FormEditorSidePanel";
import { makeEventListener } from "@solid-primitives/event-listener";
import { t } from "i18next";

type DefaultValues<T, DTO, V> = Pick<
  BaseField<T, DTO, V>,
  | "FavouriteMenuItem"
  | "MenuItem"
  | "PreviewItem"
  | "OverlayItem"
  | "init"
  | "canAddToForm"
  | "label"
  | "displayLabel"
  | "valueReferenceOptions"
  | "toDto"
  | "fromDto"
  | "postProcessFromDto"
  | "dtoSchema"
  | "duplicateProperties"
>;

const defaultDtoSchema = unknown();
export type DefaultDtoType = InferOutput<typeof defaultDtoSchema>;

type FieldWith<T, X extends keyof DefaultValues<T, DTO, V>, DTO, V> = Field<T, DTO, V> &
  Pick<DefaultValues<T, DTO, V>, X>;

export type Field<T, DTO, V = unknown> = PartialBy<BaseField<T, DTO, V>, keyof DefaultValues<T, DTO, V>>;

const MAX_FAVOURITES = 500;

export const initField = <T, DTO, V>(field: Field<T, DTO, V>): BaseField<T, DTO, V> => ({
  ...createDefaultValues(field),
  ...field,
});

const createDefaultInit =
  <T, DTO, V>(field: FieldWith<T, "label", DTO, V>) =>
  (scope: FormScope, props?: Partial<T>) => {
    const { t } = useTranslation();

    const getLabelText = (field: FieldWith<T, "label", DTO, V>) => {
      switch (field.label) {
        case "no_label":
          return {};
        case "not_required":
          return { label_text: "" };
        case "default":
          return { label_text: t(field.titleKey) };
      }
    };

    const defaultProperties = { ...getLabelText(field), ...field.defaultProperties() };
    return {
      uid: FieldId.random(),
      type: field.type,
      widget: field.widget,
      scope,
      state: "unpublished" as FieldState,
      properties: { ...defaultProperties, ...props },
    };
  };

const createDefaultMenuItem =
  <T, DTO, V>(field: Field<T, DTO, V>) =>
  (props: MenuItemProps) => {
    const { t } = useTranslation();

    return (
      <DraggableField
        label={t(field.titleKey)}
        description={t(field.descriptionKey)}
        icon={field.icon}
        type={field.type}
        category={field.category}
        onDblClick={() => {
          props.addFunction(field.type);
        }}
      />
    );
  };

const createDefaultFavouriteMenuItem =
  <T, DTO, V>(field: Field<T, DTO, V>) =>
  (props: FavouriteMenuItemProps) => {
    const { t } = useTranslation();

    return (
      <DraggableFavourite
        label={props.label}
        description={t(field.descriptionKey)}
        icon={field.icon}
        type={field.type}
        category={field.category}
        onDblClick={() => {
          props.addFunction(field.type);
        }}
        favourite={props.favourite}
        onEdit={props.onEdit}
        onDelete={props.onDelete}
      />
    );
  };

const createDefaultPreviewItem =
  <T, DTO, V>(field: FieldWith<T, "displayLabel", DTO, V>) =>
  (props: PreviewItemProps) => {
    const { t } = useTranslation();
    const { open } = useModalManager();
    const { fieldService, validationService } = useFormServices();
    const fieldConfig = createMemo(() =>
      fieldService.getField<
        FieldDataNameProperties & { required?: boolean; remember_input?: boolean } & FieldLabelTextProperties & T
      >(props.uid)!,
    );
    const issues = createMemo(() => validationService.getIssuesForField(props.uid));
    const hasErrors = createMemo(() => !isEmpty(issues()));
    const owner = getOwner();
    const [addingFavourite, setAddingFavourite] = createSignal<"closed" | "open" | "limit_reached">("closed");
    const [, setQuery] = useSearchParams<EditorSearchParams>();
    const favouriteCount = createMemo(() => FavouriteService.getAll().data?.length);
    const { selectedPanel, setSelectedPanel } = useFormEditorSidePanel();
    const { currentScope } = useFormServices();

    const createFavouriteSchema = object({
      name: requireText(MAX_CHARACTERS_SM),
    });

    const editEventHandler = () =>
      document.getElementById(props.uid.toString())?.scrollIntoView({ behavior: "smooth", block: "center" });

    createEffect(() => {
      makeEventListener(document, `field.edit-${props.uid.toString()}`, editEventHandler, {
        passive: true,
      });
    });

    createEffect(() => {
      if (addingFavourite() === "open") {
        open({
          content: () => (
            <InputModal<InferOutput<typeof createFavouriteSchema>>
              variant="default"
              title={t(($) => $.FAVOURITE_ADD_MODAL_TITLE)}
              schema={createFavouriteSchema}
              initialValue={{ name: undefined }}
              confirmLabel={t(($) => $.CREATE_BUTTON)}
              cancelLabel={t(($) => $.CANCEL_BUTTON)}
              onConfirm={({ name }) => {
                // oxlint-disable-next-line solid/reactivity
                void runWithOwner(owner, async () => {
                  await FavouriteService.create({
                    widgetUid: field.widget,
                    name,
                    defaultProps: field.defaultProperties(),
                    currentProps: fieldConfig().properties,
                  });
                });
                setQuery({ tab: "favourites" });
              }}
            >
              {({ value, updateValue, error }) => (
                <TextInput
                  required
                  label={t(($) => $.FAVOURITE_ADD_MODAL_INPUT_LABEL)}
                  value={value("name")}
                  onBlur={(e) => {
                    updateValue("name", e.target.value);
                  }}
                  onInput={(e) => {
                    updateValue("name", e.target.value);
                  }}
                  errorMessage={error("name")}
                />
              )}
            </InputModal>
          ),
          onClose: () => setAddingFavourite("closed"),
        });
      } else if (addingFavourite() === "limit_reached") {
        open({
          content: () => (
            <InformativeModal
              title={t(($) => $.FAVOURITE_LIMIT_MODAL_TITLE)}
              description={t(($) => $.FAVOURITE_LIMIT_MODAL_DESCRIPTION)}
              confirmLabel={t(($) => $.FAVOURITE_LIMIT_MODAL_BUTTON)}
              inlineMessage={t(($) => $.FAVOURITE_LIMIT_MODAL_INLINE, { count: MAX_FAVOURITES })}
              closeAriaLabel={t(($) => $.CLOSE_ARIA)}
            />
          ),
          onClose: () => setAddingFavourite("closed"),
        });
      }
    });

    const [dragDropState, { draggableActivators }] = useDragDropContext();
    const isActive = createMemo(() => {
      const panel = selectedPanel();
      if (panel?.type === "field") {
        return panel.fieldConfig.uid.equals(fieldConfig().uid);
      }
      return false;
    });

    return (
      <div class="group/field relative isolate">
        <div
          class="relative flex min-h-25 w-full rounded-lg border bg-surface-default py-6 pr-6 pl-3 text-primary hover:bg-surface-hover has-[[data-container-trigger]:focus-visible]:outline-default"
          classList={{
            "border-subtle": !hasErrors(),
            "border-error": hasErrors(),
            "shadow-elevation-light-1 hover:shadow-elevation-light-2": !isActive(),
            "shadow-elevation-light-2 outline-default": isActive(),
            "cursor-grabbing": !isNil(dragDropState.active.draggableId),
          }}
          {...draggableActivators(props.uid.toString(), true)}
        >
          <button
            data-container-trigger
            class="absolute inset-0 rounded-lg outline-none"
            classList={{ "cursor-grabbing": !isNil(dragDropState.active.draggableId) }}
            aria-label={t(($) => $.FIELD_EDIT_PROPERTIES_ARIA_LABEL, {
              field_label: field.displayLabel(fieldConfig().properties),
            })}
            onClick={() => {
              setSelectedPanel({
                type: "field",
                field: field as unknown as BaseField<unknown, unknown>,
                fieldConfig: fieldConfig(),
              });
              const panelCloseButton = document.querySelector(
                `[data-id=${getPanelCloseButtonId(currentScope)}]`,
              ) as HTMLButtonElement;
              panelCloseButton?.focus();
            }}
          />
          <div class="pointer-events-none flex w-full items-center gap-x-3">
            <div class="pointer-events-auto relative cursor-grab">
              <Handlebar expandDragArea />
            </div>
            <div inert class="w-full min-w-0 text-left">
              <Show when={field.PreviewContent} fallback={t(field.titleKey)}>
                <field.PreviewContent uid={fieldConfig().uid} properties={fieldConfig().properties} />
              </Show>
            </div>
          </div>
        </div>

        <FloatingToolbar
          classList={{
            "opacity-0 transition-opacity group-focus-within/field:opacity-100 group-hover/field:opacity-100": true,
            "opacity-100": isActive(),
          }}
          options={[
            {
              icon: "trash",
              ariaLabel: t(($) => $.DELETE_BUTTON),
              onClick: () => {
                fieldService.removeField(props.uid);
                const panel = selectedPanel();
                const isPanelOpenForDeletedField =
                  panel?.type === "field" && panel.fieldConfig.uid.equals(fieldConfig().uid);
                if (isPanelOpenForDeletedField) {
                  setSelectedPanel(null);
                }
              },
            },
            ...(field.type !== "unknown"
              ? [
                  {
                    icon: "duplicate" as const,
                    ariaLabel: t(($) => $.DUPLICATE_BUTTON),
                    onClick: () => fieldService.duplicateField(props.uid),
                  },
                  {
                    icon: "star" as const,
                    ariaLabel: t(($) => $.FAVOURITE_BUTTON),
                    onClick: () => {
                      const count = favouriteCount();
                      if (!isNil(count)) {
                        setAddingFavourite(count < MAX_FAVOURITES ? "open" : "limit_reached");
                      }
                    },
                  },
                ]
              : []),
          ]}
        />
      </div>
    );
  };

const createDefaultOverlayItem =
  <T, DTO, V>(field: Field<T, DTO, V>) =>
  () => {
    const { t } = useTranslation();

    return <FieldDragOverlay label={t(field.titleKey)} icon={field.icon} category={field.category} />;
  };

const createDefaultValues = <T, DTO, V>(field: Field<T, DTO, V>): DefaultValues<T, DTO, V> => {
  const defaults = {
    label: "default" as Label,
    displayLabel: (properties: T & FieldLabelTextProperties): string => {
      if (!isNil(properties.label_text) && !isEmpty(properties.label_text)) {
        return properties.label_text;
      }
      return t(field.titleKey);
    },
  };
  const fieldWithDefaults = { ...defaults, ...field };
  const dtoSchema = field.dtoSchema ?? defaultDtoSchema;

  return {
    init: createDefaultInit(fieldWithDefaults),
    canAddToForm: () => true,
    MenuItem: createDefaultMenuItem(field),
    FavouriteMenuItem: createDefaultFavouriteMenuItem(field),
    PreviewItem: createDefaultPreviewItem(fieldWithDefaults),
    OverlayItem: createDefaultOverlayItem(field),
    duplicateProperties: (properties) => properties,
    dtoSchema: dtoSchema as GenericSchema<DTO>,
    valueReferenceOptions: (id, props) => {
      if (!field.hasData) {
        return [];
      }
      const referenceId = new FieldValueReference(id);
      const icon = getDynamicValueIcon(referenceId, field.type);
      return [
        {
          variant: "dynamicValue",
          value: referenceId,
          label: defaults.displayLabel(props),
          pill: props.data_name,
          icon,
        },
      ];
    },
    toDto: (domain: T) => {
      const { output } = safeParse(dtoSchema, domain);
      return output as DTO;
    },
    fromDto: (dto: DTO) => {
      const { output } = safeParse(field.schema, dto);
      return output as T;
    },
    postProcessFromDto: (domain) => domain,
    ...defaults,
  };
};
